/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Security Overview Dashboard Page
 * ============================================================================
 *
 * Dashboard for monitoring platform security metrics including failed logins,
 * locked accounts, active sessions, and MFA adoption rates.
 *
 * ============================================================================
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Shield,
  RefreshCw,
  AlertTriangle,
  Lock,
  Unlock,
  Users,
  Key,
  Monitor,
  MapPin,
} from 'lucide-react';
import {
  useSecurityOverview,
  useLockedAccounts,
  useAdminUnlockAccount,
} from '@/hooks/api/useSecurity';
import { formatDistanceToNow } from 'date-fns';

export function SecurityOverviewPage() {
  const { t } = useTranslation('platform');
  const [activeTab, setActiveTab] = useState<'overview' | 'locked' | 'sessions'>('overview');

  const { data: overview, isLoading, error, refetch } = useSecurityOverview();
  const { data: lockedAccounts, refetch: refetchLocked } = useLockedAccounts();
  const unlockMutation = useAdminUnlockAccount();

  const handleUnlock = (userId: string) => {
    unlockMutation.mutate(userId, {
      onSuccess: () => {
        refetch();
        refetchLocked();
      },
    });
  };

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="loading-state">
          <RefreshCw className="animate-spin" size={24} />
          <span>{t('common.loading')}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="error-state">
          <AlertTriangle size={24} />
          <span>{t('common.error')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <h1 className="page-title">
            <Shield className="title-icon" />
            {t('security.title')}
          </h1>
          <div className="header-actions">
            <button className="btn-secondary" onClick={() => refetch()}>
              <RefreshCw size={16} />
              {t('common.refresh')}
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {overview && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon warning">
              <AlertTriangle size={20} />
            </div>
            <div className="stat-content">
              <span className="stat-value warning-value">{overview.failedLogins24h}</span>
              <span className="stat-label">{t('security.stats.failedLogins24h')}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <AlertTriangle size={20} />
            </div>
            <div className="stat-content">
              <span className="stat-value">{overview.failedLogins7d}</span>
              <span className="stat-label">{t('security.stats.failedLogins7d')}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon error">
              <Lock size={20} />
            </div>
            <div className="stat-content">
              <span className="stat-value error-value">{overview.lockedAccountsCount}</span>
              <span className="stat-label">{t('security.stats.lockedAccounts')}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon success">
              <Users size={20} />
            </div>
            <div className="stat-content">
              <span className="stat-value">{overview.activeSessionsCount}</span>
              <span className="stat-label">{t('security.stats.activeSessions')}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon brand">
              <Key size={20} />
            </div>
            <div className="stat-content">
              <span className="stat-value brand-value">{overview.mfaAdoptionRate.toFixed(1)}%</span>
              <span className="stat-label">{t('security.stats.mfaAdoption')}</span>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="tabs">
        <button
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          {t('security.tabs.overview')}
        </button>
        <button
          className={`tab ${activeTab === 'locked' ? 'active' : ''}`}
          onClick={() => setActiveTab('locked')}
        >
          {t('security.tabs.lockedAccounts')}
          {overview && overview.lockedAccountsCount > 0 && (
            <span className="tab-badge">{overview.lockedAccountsCount}</span>
          )}
        </button>
        <button
          className={`tab ${activeTab === 'sessions' ? 'active' : ''}`}
          onClick={() => setActiveTab('sessions')}
        >
          {t('security.tabs.sessions')}
        </button>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && overview && (
        <div className="two-columns">
          {/* Top Failed IPs */}
          <div className="section">
            <h2 className="section-title">
              <MapPin size={16} />
              {t('security.topFailedIps')}
            </h2>
            <div className="list-container">
              {overview.topFailedIps.map((ip, index) => (
                <div key={ip.ipAddress} className="list-item">
                  <span className="item-rank">#{index + 1}</span>
                  <span className="item-ip">{ip.ipAddress}</span>
                  <span className="item-count error">{ip.failureCount} {t('security.failures')}</span>
                </div>
              ))}
              {overview.topFailedIps.length === 0 && (
                <div className="empty-list">{t('security.noFailedIps')}</div>
              )}
            </div>
          </div>

          {/* Top Failed Users */}
          <div className="section">
            <h2 className="section-title">
              <Users size={16} />
              {t('security.topFailedUsers')}
            </h2>
            <div className="list-container">
              {overview.topFailedUsers.map((user, index) => (
                <div key={user.userId} className="list-item">
                  <span className="item-rank">#{index + 1}</span>
                  <div className="item-user">
                    <span className="user-email">{user.email}</span>
                    {user.isLocked && (
                      <span className="locked-badge">
                        <Lock size={10} /> {t('security.locked')}
                      </span>
                    )}
                  </div>
                  <span className="item-count error">{user.failureCount} {t('security.failures')}</span>
                </div>
              ))}
              {overview.topFailedUsers.length === 0 && (
                <div className="empty-list">{t('security.noFailedUsers')}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Locked Accounts Tab */}
      {activeTab === 'locked' && (
        <div className="section">
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('security.columns.email')}</th>
                  <th>{t('security.columns.lockedAt')}</th>
                  <th>{t('security.columns.unlockAt')}</th>
                  <th>{t('security.columns.failedAttempts')}</th>
                  <th>{t('security.columns.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {lockedAccounts?.map((account) => (
                  <tr key={account.userId}>
                    <td>
                      <span className="user-email">{account.email}</span>
                    </td>
                    <td className="time-cell">
                      {account.lockedAt
                        ? formatDistanceToNow(new Date(account.lockedAt), { addSuffix: true })
                        : '-'}
                    </td>
                    <td className="time-cell">
                      {account.unlockAt
                        ? formatDistanceToNow(new Date(account.unlockAt), { addSuffix: true })
                        : t('security.permanent')}
                    </td>
                    <td>
                      <span className="attempts-badge">{account.failedAttempts}</span>
                    </td>
                    <td>
                      <button
                        className="btn-unlock"
                        onClick={() => handleUnlock(account.userId)}
                        disabled={unlockMutation.isPending}
                      >
                        <Unlock size={14} />
                        {t('security.unlock')}
                      </button>
                    </td>
                  </tr>
                ))}
                {(!lockedAccounts || lockedAccounts.length === 0) && (
                  <tr>
                    <td colSpan={5} className="empty-state">
                      {t('security.noLockedAccounts')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Sessions Tab */}
      {activeTab === 'sessions' && overview && (
        <div className="section">
          <h2 className="section-title">
            <Monitor size={16} />
            {t('security.sessionsByOrg')}
          </h2>
          <div className="sessions-grid">
            {overview.sessionsByOrg.map((org) => (
              <div key={org.orgId} className="session-card">
                <div className="session-org">
                  <span className="org-name">{org.orgName}</span>
                  <span className="org-id">{org.orgId}</span>
                </div>
                <div className="session-count">
                  <Monitor size={16} />
                  <span>{org.sessionCount} {t('security.sessions')}</span>
                </div>
              </div>
            ))}
            {overview.sessionsByOrg.length === 0 && (
              <div className="empty-sessions">{t('security.noActiveSessions')}</div>
            )}
          </div>
        </div>
      )}

      <style>{pageStyles}</style>
    </div>
  );
}

const pageStyles = `
  .page-container {
    padding: 24px;
    max-width: 1400px;
    margin: 0 auto;
  }

  .page-header {
    margin-bottom: 24px;
  }

  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .page-title {
    font-size: 24px;
    font-weight: 600;
    color: var(--color-text-primary);
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .title-icon {
    color: var(--color-brand);
  }

  .header-actions {
    display: flex;
    gap: 8px;
  }

  .btn-secondary {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    border: 1px solid var(--color-border);
    background: var(--color-bg-secondary);
    color: var(--color-text-primary);
    border-radius: 6px;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .btn-secondary:hover {
    background: var(--color-bg-hover);
  }

  /* Stats Grid */
  .stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 16px;
    margin-bottom: 32px;
  }

  .stat-card {
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 8px;
    padding: 16px;
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .stat-icon {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    background: var(--color-bg-tertiary, #F3F4F6);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--color-text-secondary);
  }

  .stat-icon.warning { background: #FEF3C7; color: #B45309; }
  .stat-icon.error { background: #FEE2E2; color: #B91C1C; }
  .stat-icon.success { background: #D1FAE5; color: #047857; }
  .stat-icon.brand { background: var(--color-brand-subtle, #EEF2FF); color: var(--color-brand); }

  .stat-content {
    display: flex;
    flex-direction: column;
  }

  .stat-value {
    font-size: 20px;
    font-weight: 600;
    color: var(--color-text-primary);
  }

  .stat-value.warning-value { color: var(--color-warning, #F59E0B); }
  .stat-value.error-value { color: var(--color-error, #DC2626); }
  .stat-value.brand-value { color: var(--color-brand); }

  .stat-label {
    font-size: 12px;
    color: var(--color-text-secondary);
  }

  /* Tabs */
  .tabs {
    display: flex;
    gap: 4px;
    margin-bottom: 24px;
    border-bottom: 1px solid var(--color-border-subtle);
    padding-bottom: 0;
  }

  .tab {
    padding: 12px 20px;
    border: none;
    background: none;
    font-size: 13px;
    font-weight: 500;
    color: var(--color-text-secondary);
    cursor: pointer;
    border-bottom: 2px solid transparent;
    margin-bottom: -1px;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: all 0.15s ease;
  }

  .tab:hover {
    color: var(--color-text-primary);
  }

  .tab.active {
    color: var(--color-brand);
    border-bottom-color: var(--color-brand);
  }

  .tab-badge {
    background: var(--color-error, #DC2626);
    color: white;
    font-size: 10px;
    font-weight: 600;
    padding: 2px 6px;
    border-radius: 10px;
    min-width: 18px;
    text-align: center;
  }

  /* Section */
  .section {
    margin-bottom: 32px;
  }

  .section-title {
    font-size: 16px;
    font-weight: 600;
    color: var(--color-text-primary);
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  /* Two Columns Layout */
  .two-columns {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;
  }

  /* List Container */
  .list-container {
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 8px;
    overflow: hidden;
  }

  .list-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    border-bottom: 1px solid var(--color-border-subtle);
  }

  .list-item:last-child {
    border-bottom: none;
  }

  .item-rank {
    font-size: 11px;
    font-weight: 600;
    color: var(--color-brand);
    background: var(--color-brand-subtle, #EEF2FF);
    padding: 2px 6px;
    border-radius: 4px;
    min-width: 28px;
    text-align: center;
  }

  .item-ip {
    flex: 1;
    font-family: monospace;
    font-size: 13px;
  }

  .item-user {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .user-email {
    font-size: 13px;
  }

  .locked-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 10px;
    color: var(--color-error, #DC2626);
    background: #FEE2E2;
    padding: 1px 6px;
    border-radius: 4px;
    width: fit-content;
  }

  .item-count {
    font-size: 12px;
    font-weight: 500;
  }

  .item-count.error {
    color: var(--color-error, #DC2626);
  }

  .empty-list {
    padding: 24px;
    text-align: center;
    color: var(--color-text-muted);
    font-size: 13px;
  }

  /* Table */
  .table-container {
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 8px;
    overflow: hidden;
  }

  .data-table {
    width: 100%;
    border-collapse: collapse;
  }

  .data-table th {
    text-align: left;
    padding: 12px 16px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    color: var(--color-text-secondary);
    background: var(--color-bg-tertiary, #F9FAFB);
    border-bottom: 1px solid var(--color-border-subtle);
  }

  .data-table td {
    padding: 12px 16px;
    font-size: 13px;
    color: var(--color-text-primary);
    border-bottom: 1px solid var(--color-border-subtle);
  }

  .data-table tr:last-child td {
    border-bottom: none;
  }

  .data-table tr:hover {
    background: var(--color-bg-hover);
  }

  .time-cell {
    color: var(--color-text-secondary);
    font-size: 12px;
  }

  .attempts-badge {
    display: inline-block;
    padding: 2px 8px;
    background: #FEE2E2;
    color: #B91C1C;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
  }

  .btn-unlock {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border: 1px solid var(--color-success, #10B981);
    background: transparent;
    color: var(--color-success, #10B981);
    border-radius: 4px;
    font-size: 12px;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .btn-unlock:hover:not(:disabled) {
    background: var(--color-success, #10B981);
    color: white;
  }

  .btn-unlock:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .empty-state {
    text-align: center;
    color: var(--color-text-muted);
    padding: 40px 16px !important;
  }

  /* Sessions Grid */
  .sessions-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 16px;
  }

  .session-card {
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 8px;
    padding: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .session-org {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .org-name {
    font-weight: 500;
    font-size: 14px;
  }

  .org-id {
    font-size: 11px;
    font-family: monospace;
    color: var(--color-text-muted);
  }

  .session-count {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: var(--color-text-secondary);
  }

  .empty-sessions {
    grid-column: span 3;
    text-align: center;
    color: var(--color-text-muted);
    padding: 40px;
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 8px;
  }

  /* Loading/Error States */
  .loading-state,
  .error-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 60px;
    color: var(--color-text-secondary);
  }

  .error-state {
    color: var(--color-error, #DC2626);
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  .animate-spin {
    animation: spin 1s linear infinite;
  }

  @media (max-width: 768px) {
    .page-container {
      padding: 16px;
    }

    .header-content {
      flex-direction: column;
      align-items: flex-start;
      gap: 16px;
    }

    .stats-grid {
      grid-template-columns: repeat(2, 1fr);
    }

    .two-columns {
      grid-template-columns: 1fr;
    }

    .sessions-grid {
      grid-template-columns: 1fr;
    }
  }
`;

export default SecurityOverviewPage;
